'use client';

import React, { useState, useRef } from 'react';

const categories = [
  { id: 'all', label: 'All', icon: '🏠' },
  { id: 'hotels', label: 'Hotels', icon: '🏨' },
  { id: 'villas', label: 'Villas', icon: '🏡' },
  { id: 'apartments', label: 'Apartments', icon: '🏢' },
  { id: 'lodges', label: 'Lodges', icon: '🌿' },
  { id: 'resorts', label: 'Resorts', icon: '🌴' },
  { id: 'guesthouses', label: 'Guest Houses', icon: '🏘️' },
  { id: 'cottages', label: 'Cottages', icon: '🛖' },
  { id: 'boutique', label: 'Boutique', icon: '✨' },
  { id: 'camps', label: 'Camps', icon: '⛺' },
  { id: 'hostels', label: 'Hostels', icon: '🎒' },
  { id: 'serviced', label: 'Serviced Apts', icon: '🏙️' },
];

interface CategoryBarProps {
  onCategoryChange?: (category: string) => void;
}

export default function CategoryBar({ onCategoryChange }: CategoryBarProps) {
  const [active, setActive] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleSelect = (id: string) => {
    setActive(id);
    onCategoryChange?.(id);
  };

  const scroll = (dir: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === 'right' ? 200 : -200, behavior: 'smooth' });
    }
  };

  return (
    <div className="sticky top-[72px] z-40 bg-white border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-3 py-3">
          {/* Left scroll */}
          <button
            onClick={() => scroll('left')}
            className="hidden md:flex flex-shrink-0 w-8 h-8 items-center justify-center rounded-full border border-gray-200 hover:border-gray-400 bg-white shadow-sm transition-all"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Scrollable categories */}
          <div
            ref={scrollRef}
            className="flex items-center gap-6 overflow-x-auto scrollbar-hide flex-1 py-1"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleSelect(cat.id)}
                className={`flex flex-col items-center gap-1.5 flex-shrink-0 pb-2 border-b-2 transition-all duration-200 group ${
                  active === cat.id
                    ? 'border-gray-800 opacity-100' :'border-transparent opacity-60 hover:opacity-100 hover:border-gray-300'
                }`}
              >
                <span className="text-2xl">{cat.icon}</span>
                <span className={`text-xs font-semibold whitespace-nowrap ${active === cat.id ? 'text-gray-800' : 'text-gray-600'}`}>
                  {cat.label}
                </span>
              </button>
            ))}
          </div>

          {/* Right scroll */}
          <button
            onClick={() => scroll('right')}
            className="hidden md:flex flex-shrink-0 w-8 h-8 items-center justify-center rounded-full border border-gray-200 hover:border-gray-400 bg-white shadow-sm transition-all"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Filters button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex-shrink-0 flex items-center gap-2 border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-700 hover:border-gray-800 transition-all bg-white shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
            </svg>
            Filters
          </button>
        </div>
      </div>
    </div>
  );
}
